import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { leadSourceAdminAPI, serviceAPI, userAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import {
  AlertTriangle, ArrowRight, Users, Flame, PhoneCall, BarChart3, Home, IndianRupee,
  Clock, Info, ShieldAlert, Activity, UserX, PhoneOff, Timer, FileWarning, CalendarDays,
  MapPin, Briefcase, Layers, GitBranch, CheckSquare, CircleDot, HelpCircle, Wallet, UserCheck, Target
} from 'lucide-react';

const PERIODS = [['today', 'Today'], ['week', 'Week'], ['month', 'Month'], ['year', 'Year'], ['custom', 'Custom']];
const money = (v) => (v == null ? '—' : `₹${(v / 100000).toFixed(1)}L`);
const num = (v) => (v == null ? '—' : v.toLocaleString('en-IN'));
const greet = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good Morning,' : h < 17 ? 'Good Afternoon,' : 'Good Evening,';
};
const dt = (v, withTime = true) => (v
  ? new Date(v).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {})
  })
  : '—');

const DONUT_COLORS = ['#2563eb', '#ec4899', '#f59e0b', '#8b5cf6', '#10b981', '#94a3b8'];
const STATUS_COLORS = ['#10b981', '#3b82f6', '#ef4444', '#f59e0b', '#f97316', '#94a3b8'];

const Card = ({ title, info, action, onAction, actionLabel = 'View All', children, testid, right, className = '' }) => (
  <section data-testid={testid}
    className={`flex h-full min-w-0 flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.06)] dark:border-slate-800 dark:bg-slate-900 ${className}`}>
    {(title || right) && (
      <header className="mb-3 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <h2 className="truncate text-base font-bold text-slate-900 dark:text-slate-100">{title}</h2>
          {info && <Info className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {right}
          {action && (
            <button data-testid={`${testid}-view-all`} onClick={onAction}
              className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 transition-colors hover:text-blue-800">
              {actionLabel}<ArrowRight className="h-3 w-3" />
            </button>
          )}
        </div>
      </header>
    )}
    <div className="min-w-0 flex-1">{children}</div>
  </section>
);

const Donut = ({ segments, total, label, testid }) => {
  const sum = segments.reduce((s, x) => s + (x.value || 0), 0) || 1;
  let offset = 0;
  const r = 52, c = 2 * Math.PI * r;
  return (
    <div className="relative mx-auto h-[150px] w-[150px] shrink-0" data-testid={testid}>
      <svg viewBox="0 0 140 140" className="h-full w-full -rotate-90">
        <circle cx="70" cy="70" r={r} fill="none" stroke="#f1f5f9" strokeWidth="18" />
        {segments.map((s, i) => {
          const len = ((s.value || 0) / sum) * c;
          const el = (
            <circle key={i} cx="70" cy="70" r={r} fill="none" stroke={s.color} strokeWidth="18"
              strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset} strokeLinecap="butt" />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-extrabold leading-none text-slate-900 dark:text-white">{num(total)}</span>
        <span className="text-[11px] text-slate-500">{label}</span>
      </div>
    </div>
  );
};

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [period, setPeriod] = useState('month');
  const [range, setRange] = useState({ from: '', to: '' });
  const [filters, setFilters] = useState({ executive: '', service: '', source: '', project_status: '', city: '' });
  const [team, setTeam] = useState('sales');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opts, setOpts] = useState({ users: [], services: [], sources: [] });
  const isSuperAdmin = user?.role === 'super_admin';
  // Single Super-Admin-only reporting switch. Default OFF = real business data only.
  const [includeDemo, setIncludeDemo] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period, team, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
      if (period === 'custom' && range.from) { params.date_from = range.from; params.date_to = range.to || range.from; }
      if (includeDemo) params.include_demo = true;
      const r = await api.get('/dashboard/management', { params });
      setData(r.data);
    } finally { setLoading(false); }
  }, [period, team, filters, range, includeDemo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // users.view is admin-only: skip the call for roles without it instead of eating a 403
    const usersReq = hasPermission('users.view')
      ? userAPI.getAll().catch(() => ({ data: [] })) : Promise.resolve({ data: [] });
    Promise.all([usersReq, serviceAPI.getAll().catch(() => ({ data: [] })),
      leadSourceAdminAPI.listAll().catch(() => ({ data: [] }))])
      .then(([u, s, ls]) => setOpts({
        users: (u.data || []).filter((x) => x.is_active !== false),
        services: s.data || [], sources: ls.data || []
      }));
  }, [hasPermission]);

  const cities = useMemo(() => [...new Set((opts.users || []).map((u) => u.city).filter(Boolean))], [opts.users]);
  const go = (path) => navigate(path);
  const A = data?.attention || {};
  const O = data?.overview || {};
  const ageing = data?.lead_ageing || [];
  const ageMax = Math.max(1, ...ageing.map((b) => b.count));

  const attentionCards = [
    // Counts overdue follow-up ENTRIES (a lead can have more than one). The Leads page "Overdue"
    // chip counts overdue LEADS, so the two figures can legitimately differ.
    ['overdue_followups', 'Overdue Follow-ups (entries)', PhoneCall, 'bg-red-100 text-red-600', 'text-red-600', '/work-queue'],
    ['unassigned_leads', 'Unassigned Leads', UserX, 'bg-rose-100 text-rose-600', 'text-rose-600', '/leads?assigned=unassigned'],
    ['not_contacted_3d', 'Unattended (no first action)', PhoneOff, 'bg-amber-100 text-amber-600', 'text-amber-600', '/leads?filter=not_contacted'],
    ['delayed_projects', 'Delayed Projects', Timer, 'bg-yellow-100 text-yellow-600', 'text-yellow-600', '/projects?status=delayed'],
    ['payment_blocked', 'Payment Blocked', ShieldAlert, 'bg-indigo-100 text-indigo-600', 'text-indigo-600', '/finance?filter=outstanding'],
    ['client_approvals_pending', 'Client Approvals Pending', FileWarning, 'bg-violet-100 text-violet-600', 'text-violet-600', '/architect?card=awaiting_client_approval'],
    ['lead_leakage', 'Lead Leakage', AlertTriangle, 'bg-teal-100 text-teal-600', 'text-teal-600', '/leads?filter=leakage'],
  ];
  const kpis = [
    ['Total Leads', num(O.total_leads), Users, 'bg-blue-100 text-blue-600', O.total_leads_delta, '/leads'],
    ['Hot Leads (Stage)', num(O.hot_leads), Flame, 'bg-red-100 text-red-500', null, '/leads?status=hot_lead'],
    ['Follow-ups Today', num(O.followups_today), PhoneCall, 'bg-sky-100 text-sky-600', null, '/work-queue'],
    ['Converted', num(O.converted), BarChart3, 'bg-emerald-100 text-emerald-600', O.converted_delta, '/leads?status=converted_group'],
    ['Total Projects', num(O.total_projects), Home, 'bg-violet-100 text-violet-600', null, '/projects'],
    ['Total Business (Booked)', data?.can_view_finance ? money(O.total_business) : 'Restricted', IndianRupee, 'bg-amber-100 text-amber-600', null, '/finance'],
  ];

  const filterDefs = [
    // no users.view => the list is always empty, so hide the control instead of showing a dead select
    ...(opts.users.length ? [['executive', 'Executive', 'All Executives', UserCheck, opts.users.map((u) => [u.id, u.name])]] : []),
    ['service', 'Service', 'All Services', Layers, opts.services.map((s) => [s.name, s.name])],
    ['source', 'Lead Source', 'All Sources', GitBranch, opts.sources.map((s) => [s.name, s.name])],
    ['project_status', 'Project Status', 'All Statuses', Briefcase, (data?.project_status || []).map((p) => [p.status, p.status.replace(/_/g, ' ')])],
    ['city', 'City / Location', 'All Cities', MapPin, cities.map((c) => [c, c])],
  ];

  const funnelColors = ['#3b82f6', '#22c55e', '#f97316', '#8b5cf6', '#10b981'];
  const alertRows = [
    ['milestone_overdue', 'Milestone Overdue', 'bg-red-100 text-red-600', '/architect?card=overdue'],
    ['due_this_week', 'Due This Week', 'bg-orange-100 text-orange-600', '/architect?card=due_soon'],
    ['payment_blocked', 'Payment Blocked', 'bg-rose-100 text-rose-600', '/finance?filter=outstanding'],
    ['client_approval_pending', 'Client Approval Pending', 'bg-amber-100 text-amber-600', '/architect?card=awaiting_client_approval'],
    ['internal_approval_pending', 'Internal Approval Pending', 'bg-violet-100 text-violet-600', '/architect?card=awaiting_internal_review'],
  ];

  const statusPill = (s = '') => {
    const k = s.toLowerCase();
    if (k.includes('hot')) return 'bg-red-100 text-red-700';
    if (k.includes('new')) return 'bg-emerald-100 text-emerald-700';
    if (k.includes('follow')) return 'bg-blue-100 text-blue-700';
    if (k.includes('qualified') || k.includes('warm')) return 'bg-amber-100 text-amber-700';
    if (k.includes('converted') || k.includes('won')) return 'bg-green-100 text-green-700';
    return 'bg-slate-100 text-slate-600';
  };
  const prioTone = (p = '') => (p === 'high' ? 'text-red-600' : p === 'low' ? 'text-slate-500' : 'text-amber-600');
  const targetTone = (v) => (v >= 70 ? 'bg-emerald-500' : v >= 50 ? 'bg-amber-500' : v >= 40 ? 'bg-orange-500' : 'bg-red-500');

  return (
    <div className="space-y-4" data-testid="management-dashboard">
      {/* toolbar: period + quick add */}
      <section className="flex flex-wrap items-center justify-between gap-2" data-testid="dashboard-toolbar">
        <div className="flex flex-wrap items-center gap-1.5" data-testid="period-switch">
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
            <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
            {new Date().toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
          </span>
          {PERIODS.map(([k, label]) => (
            <button key={k} data-testid={`period-${k}`} onClick={() => setPeriod(k)}
              className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-colors ${period === k
                ? 'bg-[#0E1626] text-white shadow-sm'
                : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'}`}>
              {label}
            </button>
          ))}
          {period === 'custom' && (
            <span className="flex items-center gap-1">
              <input type="date" data-testid="custom-from" value={range.from}
                onChange={(e) => setRange({ ...range, from: e.target.value })}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900" />
              <input type="date" data-testid="custom-to" value={range.to}
                onChange={(e) => setRange({ ...range, to: e.target.value })}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900" />
            </span>
          )}
        </div>
        {isSuperAdmin && (
          <label data-testid="include-demo-toggle"
            className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
            <input type="checkbox" data-testid="include-demo-checkbox" checked={includeDemo}
              onChange={(e) => setIncludeDemo(e.target.checked)} className="h-3.5 w-3.5 accent-amber-500" />
            Include Demo Data
          </label>
        )}
      </section>

      {/* brand banner */}
      <section data-testid="dashboard-banner"
        className="grid grid-cols-1 overflow-hidden rounded-xl border border-amber-200/70 bg-[linear-gradient(90deg,#FDF6E9_0%,#FBEFD8_55%,#F7E7CE_100%)] shadow-[0_1px_2px_rgba(16,24,40,0.06)] lg:grid-cols-[1.25fr_1.1fr_0.85fr]">
        <div className="px-6 py-5">
          <p className="text-base font-semibold text-amber-700">{greet()}</p>
          <h1 className="mt-0.5 text-2xl font-extrabold leading-tight text-slate-900 sm:text-3xl">{user?.name || 'Welcome'}!</h1>
          <p className="mt-1.5 text-sm text-slate-600">Let's turn plans into progress.</p>
        </div>
        <div className="flex items-center px-6 py-4 lg:border-l lg:border-amber-200/70">
          <blockquote className="text-sm italic leading-relaxed text-slate-700">
            <span className="mr-1 text-2xl font-serif leading-none text-amber-500">“</span>
            Strong construction premium material se nahi, experienced team aur sahi planning se hota hai.
            <span className="mt-1 block text-right text-xs font-semibold not-italic text-slate-800">— GNONS</span>
          </blockquote>
        </div>
        <div className="relative min-h-[120px] overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
            alt="GNONS project" className="absolute inset-0 h-full w-full object-cover" data-testid="banner-image" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F7E7CE] via-transparent to-[#0E1626]/75" />
          <ul className="absolute inset-y-0 right-0 flex w-[46%] flex-col justify-center gap-0.5 pr-5 text-right text-sm font-extrabold uppercase tracking-[0.12em] text-white">
            <li>Plan</li><li>Design</li><li>Build</li><li>Grow</li>
          </ul>
        </div>
      </section>

      {/* global filters */}
      <section data-testid="dashboard-filters"
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[repeat(5,minmax(0,1fr))_140px]">
        {filterDefs.map(([key, label, allLabel, Icon, items]) => (
          <label key={key}
            className="flex h-full items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-[0_1px_2px_rgba(16,24,40,0.06)] dark:border-slate-800 dark:bg-slate-900">
            <Icon className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="min-w-0 flex-1">
              <span className="block text-[10px] font-medium uppercase tracking-wide text-slate-400">{label}</span>
              <select data-testid={`filter-${key}`} value={filters[key]}
                onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
                className="w-full cursor-pointer truncate border-0 bg-transparent p-0 text-sm font-semibold text-slate-800 outline-none dark:text-slate-100">
                <option value="">{allLabel}</option>
                {items.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </span>
          </label>
        ))}
        <button data-testid="filter-reset"
          onClick={() => setFilters({ executive: '', service: '', source: '', project_status: '', city: '' })}
          className="h-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-[0_1px_2px_rgba(16,24,40,0.06)] transition-colors hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
          Reset
        </button>
      </section>

      {/* attention required */}
      <section data-testid="attention-required"
        className="rounded-xl border border-red-200 bg-[#FEF6F5] p-4 shadow-[0_1px_2px_rgba(16,24,40,0.06)] dark:border-red-900/50 dark:bg-red-950/20">
        <header className="mb-3 flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-base font-bold text-red-600">
            <AlertTriangle className="h-5 w-5" /> Attention Required
          </h2>
          <button data-testid="attention-required-view-all" onClick={() => go('/work-queue')}
            className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800">
            View All<ArrowRight className="h-3 w-3" />
          </button>
        </header>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          {attentionCards.map(([key, label, Icon, tone, numTone, path]) => (
            <button key={key} data-testid={`attention-${key}`} onClick={() => go(path)}
              className="flex h-full items-center gap-2.5 rounded-xl border border-slate-200 bg-white p-3 text-left transition-all hover:-translate-y-0.5 hover:border-red-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-900">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone}`}>
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span data-testid={`attention-${key}-count`} className={`block text-xl font-extrabold leading-none ${numTone}`}>
                  {loading ? '…' : num(A[key])}
                </span>
                <span className="mt-1 block text-[11px] font-medium leading-tight text-slate-500 dark:text-slate-400">{label}</span>
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* business overview */}
      <section data-testid="business-overview"
        className="rounded-xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.06)] dark:border-slate-800 dark:bg-slate-900">
        <header className="mb-3 flex items-center gap-1.5">
          <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Business Overview</h2>
          <Info className="h-3.5 w-3.5 text-slate-400" />
        </header>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {kpis.map(([label, value, Icon, tone, d, path]) => (
            <button key={label} data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`} onClick={() => go(path)}
              className="flex h-full items-start gap-3 rounded-xl border border-slate-200 bg-white p-3 text-left transition-all hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-900">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                <Icon className="h-5 w-5" />
              </span>
              <span className="min-w-0">
                <span className="block text-2xl font-extrabold leading-none text-slate-900 dark:text-white">{loading ? '…' : value}</span>
                <span className="mt-1 block text-[11px] font-medium text-slate-500 dark:text-slate-400">{label}</span>
                {d?.pct != null && (
                  <span className={`mt-1 block text-[11px] font-semibold ${d.pct >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {d.pct >= 0 ? '▲' : '▼'} {Math.abs(d.pct)}%
                    <span className="ml-1 font-normal text-slate-400">vs previous</span>
                  </span>
                )}
                {label === 'Total Projects' && (
                  <span className="mt-1 flex flex-wrap items-center gap-1 text-[10px]">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      {num(O.ongoing_projects)} Ongoing
                    </span>
                    <span className="rounded bg-red-100 px-1.5 py-0.5 font-semibold text-red-600">{num(O.delayed_projects)} Delayed</span>
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* row: ageing · funnel · lead source · revenue */}
      <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 xl:grid-cols-[3.5fr_3.1fr_3fr_2.9fr]">
        <Card title="Lead Ageing" info testid="lead-ageing" action onAction={() => go('/leads?sort=ageing')}>
          <div className="flex h-[150px] items-end justify-around gap-2">
            {ageing.map((b, i) => (
              <button key={b.bucket} data-testid={`ageing-${b.bucket.replace(/\W+/g, '-')}`} onClick={() => go('/leads?sort=ageing')}
                className="flex h-full flex-1 flex-col items-center justify-end gap-1.5">
                <span className="text-sm font-extrabold text-slate-900 dark:text-white">{b.count}</span>
                <span className="w-full max-w-[52px] rounded-t-md transition-all duration-500"
                  style={{ height: `${Math.max(6, (b.count * 100) / ageMax)}%`, background: STATUS_COLORS[i] || '#94a3b8' }} />
                <span className="text-[11px] font-medium text-slate-500">{b.bucket}</span>
              </button>
            ))}
          </div>
        </Card>

        <Card title="Sales Funnel" testid="sales-funnel" action onAction={() => go('/leads')}>
          <ul className="space-y-2.5">
            {(data?.funnel || []).map((f, i) => (
              <li key={f.stage} data-testid={`funnel-${f.stage.replace(/\W+/g, '-')}`} className="flex items-center gap-2">
                <span className="w-[68px] shrink-0 truncate text-[11px] font-medium text-slate-600 dark:text-slate-300">{f.stage}</span>
                <span className="h-4 min-w-0 flex-1 overflow-hidden rounded-md bg-slate-100 dark:bg-slate-800">
                  <span className="block h-full rounded-md transition-all duration-500"
                    style={{ width: `${Math.max(2, Math.min(f.pct, 100))}%`, background: funnelColors[i] || '#3b82f6' }} />
                </span>
                <span className="w-9 shrink-0 text-right text-[11px] font-extrabold text-slate-900 dark:text-white">{f.count}</span>
                <span className="w-11 shrink-0 text-right text-[11px] text-slate-400">({f.pct}%)</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Lead Source" info testid="lead-source" action onAction={() => go('/analytics/lead-performance')}>
          <div className="flex items-center gap-3">
            <Donut testid="lead-source-donut" label="Leads" total={O.total_leads}
              segments={(data?.lead_source || []).map((s, i) => ({ value: s.count, color: DONUT_COLORS[i % DONUT_COLORS.length] }))} />
            <ul className="min-w-0 flex-1 space-y-1.5">
              {(data?.lead_source || []).map((s, i) => (
                <li key={s.source} data-testid={`source-${s.source.replace(/\W+/g, '-')}`} className="flex items-center gap-2 text-[11px]">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                  <span className="min-w-0 flex-1 truncate text-slate-600 dark:text-slate-300">{s.source}</span>
                  <span className="shrink-0 font-bold text-slate-900 dark:text-white">{s.pct}%</span>
                </li>
              ))}
              {!(data?.lead_source || []).length && <li className="text-xs text-slate-400">No lead sources in scope.</li>}
            </ul>
          </div>
        </Card>

        <Card title="Revenue & Collection" testid="revenue-collection" action onAction={() => go('/finance')}>
          {data?.revenue ? (
            <dl className="grid grid-cols-3 gap-x-2 gap-y-4">
              {[['Total Business', data.revenue.total_business, 'text-slate-900 dark:text-white'],
                ['Received', data.revenue.received, 'text-emerald-600'],
                ['Outstanding', data.revenue.outstanding, 'text-red-600'],
                ['Expenses', data.revenue.expenses, 'text-amber-600'],
                ['Net Profit', data.revenue.net_profit, 'text-emerald-600'],
                ['Collection Due (This Week)', data.revenue.collection_due_week, 'text-red-600']].map(([l, v, tone]) => (
                <div key={l} data-testid={`revenue-${l.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <dd className={`text-base font-extrabold leading-none ${tone}`}>{money(v)}</dd>
                  <dt className="mt-1 text-[10px] leading-tight text-slate-500">{l}</dt>
                </div>
              ))}
            </dl>
          ) : <p className="text-xs text-muted-foreground" data-testid="revenue-restricted">Finance data is restricted for your role.</p>}
        </Card>
      </div>

      {/* row: project status · project alerts · team performance */}
      <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2 xl:grid-cols-[3.9fr_3.3fr_5.5fr]">
        <Card title="Project Status" info testid="project-status" action onAction={() => go('/projects')}>
          <div className="flex items-center gap-4">
            <Donut testid="project-status-donut" label="Projects" total={O.total_projects}
              segments={(data?.project_status || []).map((p, i) => ({ value: p.count, color: STATUS_COLORS[i % STATUS_COLORS.length] }))} />
            <ul className="min-w-0 flex-1 space-y-1.5">
              {(data?.project_status || []).map((p, i) => (
                <li key={p.status} data-testid={`pstatus-${p.status}`}>
                  <button onClick={() => go(`/projects?status=${p.status}`)}
                    className="flex w-full items-center gap-2 text-xs transition-colors hover:text-blue-600">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: STATUS_COLORS[i % STATUS_COLORS.length] }} />
                    <span className="min-w-0 flex-1 truncate capitalize text-slate-600 dark:text-slate-300">{p.status.replace(/_/g, ' ')}</span>
                    <span className="shrink-0 font-bold text-slate-900 dark:text-white">{p.count}</span>
                  </button>
                </li>
              ))}
              {!(data?.project_status || []).length && <li className="text-xs text-slate-400">No projects in this scope.</li>}
            </ul>
          </div>
        </Card>

        <Card title="Project Alerts" info testid="project-alerts" action onAction={() => go('/projects')}>
          <ul className="space-y-2">
            {alertRows.map(([k, l, tone, path]) => (
              <li key={k}>
                <button data-testid={`alert-${k}`} onClick={() => go(path)}
                  className="flex w-full items-center gap-2.5 rounded-lg px-1 py-1.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800">
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${tone}`}>
                    <AlertTriangle className="h-3.5 w-3.5" />
                  </span>
                  <span className="w-7 shrink-0 text-sm font-extrabold text-slate-900 dark:text-white">{num(data?.project_alerts?.[k])}</span>
                  <span className="min-w-0 flex-1 truncate text-xs text-slate-600 dark:text-slate-300">{l}</span>
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <Card testid="team-performance"
          title="Team Performance"
          right={(
            <span className="flex items-center gap-1">
              {[['sales', 'Sales'], ['architect', 'Architect'], ['site', 'Site Team']].map(([k, l]) => (
                <button key={k} data-testid={`team-tab-${k}`} onClick={() => setTeam(k)}
                  className={`rounded-md px-2.5 py-1 text-[11px] font-semibold transition-colors ${team === k
                    ? 'bg-[#0E1626] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'}`}>
                  {l}
                </button>
              ))}
            </span>
          )}
          action onAction={() => go('/performance')}>
          <div className="min-w-0 max-w-full overflow-x-auto">
            <table className="w-full min-w-[560px] text-xs">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] font-semibold text-slate-500 dark:bg-slate-800/60">
                  <th className="rounded-l-md px-2 py-2">#</th><th className="px-2 py-2">Executive</th>
                  <th className="px-2 py-2">Leads</th><th className="px-2 py-2">Calls</th>
                  <th className="px-2 py-2">Follow-ups</th><th className="px-2 py-2">Conversions</th>
                  <th className="rounded-r-md px-2 py-2">Target %</th>
                </tr>
              </thead>
              <tbody>
                {(data?.team_performance?.rows || []).map((r, i) => (
                  <tr key={r.user_id} data-testid={`team-row-${r.user_id}`} className="border-b border-slate-100 last:border-0 dark:border-slate-800">
                    <td className="px-2 py-2 text-slate-400">{i + 1}</td>
                    <td className="px-2 py-2">
                      <span className="flex items-center gap-2">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                          {(r.name || '?').charAt(0).toUpperCase()}
                        </span>
                        <span className="truncate font-semibold text-slate-800 dark:text-slate-100">{r.name}</span>
                      </span>
                    </td>
                    <td className="px-2 py-2">{r.leads}</td>
                    <td className="px-2 py-2">{r.calls}</td>
                    <td className="px-2 py-2">{r.follow_ups}</td>
                    <td className="px-2 py-2">{r.conversions}</td>
                    <td className="px-2 py-2">
                      <span className="flex items-center gap-1.5">
                        <span className="h-1.5 w-10 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                          <span className={`block h-full rounded-full ${targetTone(r.target_pct)}`} style={{ width: `${Math.min(r.target_pct, 100)}%` }} />
                        </span>
                        <span className="font-bold text-slate-800 dark:text-slate-100">{r.target_pct}%</span>
                      </span>
                    </td>
                  </tr>
                ))}
                {!(data?.team_performance?.rows || []).length && (
                  <tr><td colSpan={7} className="px-2 py-3 text-slate-400">No members in this team scope.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* row: recent leads · upcoming tasks · recent activity */}
      <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2 xl:grid-cols-[3.6fr_4.4fr_4fr]">
        <Card title="Recent Leads" info testid="recent-leads" action onAction={() => go('/leads')}>
          <div className="min-w-0 max-w-full overflow-x-auto">
            <table className="w-full min-w-[320px] text-xs">
              <thead>
                <tr className="text-left text-[11px] font-semibold text-slate-400">
                  <th className="pb-2 pr-2">Name</th><th className="pb-2 pr-2">Source</th>
                  <th className="pb-2 pr-2">Service</th><th className="pb-2 pr-2">Status</th><th className="pb-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent_leads || []).map((l) => (
                  <tr key={l.id} data-testid={`recent-lead-${l.id}`} onClick={() => go(`/leads/${l.id}`)}
                    className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800">
                    <td className="max-w-[92px] truncate py-2 pr-2 font-semibold text-slate-800 dark:text-slate-100">{l.name}</td>
                    <td className="max-w-[72px] truncate py-2 pr-2 text-slate-500">{l.source || '—'}</td>
                    <td className="max-w-[80px] truncate py-2 pr-2 text-slate-500">{l.service || '—'}</td>
                    <td className="py-2 pr-2">
                      <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold capitalize ${statusPill(l.status)}`}>
                        {(l.status || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-2 text-slate-500">{dt(l.created_at, false)}</td>
                  </tr>
                ))}
                {!(data?.recent_leads || []).length && (
                  <tr><td colSpan={5} className="py-3 text-slate-400">No leads in this scope.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card testid="upcoming-tasks" info action onAction={() => go('/work-queue')}
          title={<span className="flex items-center gap-2"><CheckSquare className="h-4 w-4 text-blue-600" />Upcoming Tasks</span>}>
          <div className="min-w-0 max-w-full overflow-x-auto">
            <table className="w-full min-w-[340px] text-xs">
              <thead>
                <tr className="text-left text-[11px] font-semibold text-slate-400">
                  <th className="pb-2 pr-2" /><th className="pb-2 pr-2">Task</th><th className="pb-2 pr-2">Related To</th>
                  <th className="pb-2 pr-2">Due Date</th><th className="pb-2">Priority</th>
                </tr>
              </thead>
              <tbody>
                {(data?.upcoming_tasks || []).map((t) => (
                  <tr key={t.id} data-testid={`task-${t.id}`} onClick={() => go(t.lead_id ? `/leads/${t.lead_id}` : '/work-queue')}
                    className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800">
                    <td className="py-2 pr-2"><span className="block h-3.5 w-3.5 rounded-[3px] border border-slate-300 dark:border-slate-600" /></td>
                    <td className="max-w-[150px] truncate py-2 pr-2 font-medium text-slate-800 dark:text-slate-100">{t.task}</td>
                    <td className="max-w-[100px] truncate py-2 pr-2 text-slate-500">{t.related_to || '—'}</td>
                    <td className="whitespace-nowrap py-2 pr-2 text-slate-500">{dt(t.due_date)}</td>
                    <td className={`py-2 text-[11px] font-semibold capitalize ${prioTone(t.priority)}`}>{t.priority}</td>
                  </tr>
                ))}
                {!(data?.upcoming_tasks || []).length && (
                  <tr><td colSpan={5} className="py-3 text-slate-400">Nothing scheduled ahead.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card testid="recent-activity" info action onAction={() => go('/activity')}
          title={<span className="flex items-center gap-2"><Activity className="h-4 w-4 text-amber-500" />Recent Activity</span>}>
          <ul className="space-y-1.5">
            {(data?.recent_activity || []).map((a, i) => {
              const Icon = [Users, GitBranch, FileWarning, Wallet, Target, CircleDot][i % 6];
              const tone = ['bg-blue-100 text-blue-600', 'bg-amber-100 text-amber-600', 'bg-violet-100 text-violet-600',
                'bg-emerald-100 text-emerald-600', 'bg-sky-100 text-sky-600', 'bg-slate-100 text-slate-500'][i % 6];
              return (
                <li key={a.id} data-testid={`activity-${a.id}`} className="flex items-center gap-2.5 text-xs">
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${tone}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">{a.activity}</span>
                  <span className="hidden max-w-[90px] shrink-0 truncate text-slate-500 sm:block">{a.user || 'System'}</span>
                  <span className="shrink-0 whitespace-nowrap text-[11px] text-slate-400">{dt(a.at)}</span>
                </li>
              );
            })}
            {!(data?.recent_activity || []).length && <li className="py-3 text-xs text-slate-400">No recent activity.</li>}
          </ul>
        </Card>
      </div>

      {/* footer */}
      <footer data-testid="dashboard-footer"
        className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 text-xs text-slate-500 dark:border-slate-800">
        <span>© {new Date().getFullYear()} GNONS Design &amp; Construction. All rights reserved.</span>
        <span className="flex items-center gap-2">
          <HelpCircle className="h-3.5 w-3.5 text-slate-400" />
          <span>Help</span><span className="text-slate-300">|</span>
          <span>Support</span><span className="text-slate-300">|</span>
          <span>Feedback</span>
        </span>
        <span className="font-serif italic text-amber-600">Building The Feature</span>
      </footer>
    </div>
  );
};

export default DashboardPage;
